/**
 * Security utilities and validation functions
 * Implements OWASP security standards for the Template Website CMS
 */

import { z } from 'zod';

// Security configuration constants
export const SECURITY_CONFIG = {
  // Password requirements
  PASSWORD_MIN_LENGTH: 12,
  PASSWORD_REQUIRE_UPPERCASE: true,
  PASSWORD_REQUIRE_LOWERCASE: true,
  PASSWORD_REQUIRE_NUMBERS: true,
  PASSWORD_REQUIRE_SYMBOLS: true,
  
  // Session security
  SESSION_TIMEOUT: 30 * 60 * 1000, // 30 minutes
  MAX_LOGIN_ATTEMPTS: 5,
  LOCKOUT_DURATION: 15 * 60 * 1000, // 15 minutes
  
  // File upload security
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'],
  ALLOWED_DOCUMENT_TYPES: ['application/pdf', 'text/plain'],
  
  // Input validation
  MAX_STRING_LENGTH: 1000,
  MAX_TEXT_LENGTH: 10000,
  MAX_URL_LENGTH: 2048,
  
  // Rate limiting
  RATE_LIMIT_WINDOW: 15 * 60 * 1000, // 15 minutes
  MAX_REQUESTS_PER_WINDOW: 100,
} as const;

// Input sanitization patterns
export const SANITIZATION_PATTERNS = {
  // Remove potentially dangerous HTML tags
  HTML_TAGS: /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
  // Remove SQL injection patterns
  SQL_INJECTION: /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION|SCRIPT)\b)/gi,
  // Remove XSS patterns
  XSS_PATTERNS: /javascript:|on\w+\s*=|<iframe|<object|<embed|<link/gi,
  // Remove path traversal patterns
  PATH_TRAVERSAL: /\.\.\/|\.\.\\|%2e%2e%2f|%2e%2e%5c/gi,
} as const;

/**
 * Sanitize user input to prevent XSS, SQL injection, and other attacks
 * @param input - The input string to sanitize
 * @returns Sanitized string
 */
export const sanitizeInput = (input: string): string => {
  if (typeof input !== 'string') return '';
  
  return input
    .replace(SANITIZATION_PATTERNS.HTML_TAGS, '')
    .replace(SANITIZATION_PATTERNS.SQL_INJECTION, '')
    .replace(SANITIZATION_PATTERNS.XSS_PATTERNS, '')
    .replace(SANITIZATION_PATTERNS.PATH_TRAVERSAL, '')
    .trim();
};

/**
 * Validate password strength according to OWASP guidelines
 * @param password - Password to validate
 * @returns Validation result
 */
export const validatePassword = (password: string): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  // NOTE: Using RegExp constructor here avoids ESLint false-positives with regex literal escaping.
  const passwordSymbolRegex = new RegExp('[!@#$%^&*()_+\\-=\\[\\]{};:\'"\\\\|,.<>/?]');
  
  if (password.length < SECURITY_CONFIG.PASSWORD_MIN_LENGTH) {
    errors.push(`Password must be at least ${SECURITY_CONFIG.PASSWORD_MIN_LENGTH} characters long`);
  }
  
  if (SECURITY_CONFIG.PASSWORD_REQUIRE_UPPERCASE && !/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  
  if (SECURITY_CONFIG.PASSWORD_REQUIRE_LOWERCASE && !/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  
  if (SECURITY_CONFIG.PASSWORD_REQUIRE_NUMBERS && !/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  
  if (SECURITY_CONFIG.PASSWORD_REQUIRE_SYMBOLS && !passwordSymbolRegex.test(password)) {
    errors.push('Password must contain at least one special character');
  }
  
  // Check for common weak passwords
  const commonPasswords = ['password', '123456', 'admin', 'qwerty', 'letmein'];
  if (commonPasswords.some(common => password.toLowerCase().includes(common))) {
    errors.push('Password contains common weak patterns');
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Validate email address format and security
 * @param email - Email to validate
 * @returns Validation result
 */
export const validateEmail = (email: string): { valid: boolean; error?: string } => {
  if (!email || typeof email !== 'string') {
    return { valid: false, error: 'Email is required' };
  }
  
  // Basic email format validation
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(email)) {
    return { valid: false, error: 'Invalid email format' };
  }
  
  // Check for suspicious patterns
  if (email.includes('..') || email.startsWith('.') || email.endsWith('.')) {
    return { valid: false, error: 'Invalid email format' };
  }
  
  // Length validation
  if (email.length > 254) {
    return { valid: false, error: 'Email address too long' };
  }
  
  return { valid: true };
};

/**
 * Validate file upload security
 * @param file - File to validate
 * @param allowedTypes - Array of allowed MIME types
 * @param maxSize - Maximum file size in bytes
 * @returns Validation result
 */
export const validateFileUpload = (
  file: File, 
  allowedTypes: string[] = SECURITY_CONFIG.ALLOWED_IMAGE_TYPES,
  maxSize: number = SECURITY_CONFIG.MAX_FILE_SIZE
): { valid: boolean; error?: string } => {
  if (!file) {
    return { valid: false, error: 'No file provided' };
  }
  
  // Check file type
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: `Invalid file type. Allowed types: ${allowedTypes.join(', ')}` };
  }
  
  // Check file size
  if (file.size > maxSize) {
    return { valid: false, error: `File too large. Maximum size: ${Math.round(maxSize / 1024 / 1024)}MB` };
  }
  
  // Check file name for suspicious patterns
  const suspiciousPatterns = /\.(exe|bat|cmd|scr|pif|com|vbs|js|jar|php|asp|jsp)$/i;
  if (suspiciousPatterns.test(file.name)) {
    return { valid: false, error: 'File type not allowed for security reasons' };
  }
  
  // Check for path traversal in filename
  if (file.name.includes('..') || file.name.includes('/') || file.name.includes('\\')) {
    return { valid: false, error: 'Invalid file name' };
  }
  
  return { valid: true };
};

/**
 * Validate URL security
 * @param url - URL to validate
 * @returns Validation result
 */
export const validateUrl = (url: string): { valid: boolean; error?: string } => {
  if (!url || typeof url !== 'string') {
    return { valid: false, error: 'URL is required' };
  }
  
  // Length validation
  if (url.length > SECURITY_CONFIG.MAX_URL_LENGTH) {
    return { valid: false, error: 'URL too long' };
  }
  
  // Protocol validation
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return { valid: false, error: 'URL must start with http:// or https://' };
  }
  
  // Check for suspicious patterns
  if (url.includes('javascript:') || url.includes('data:') || url.includes('vbscript:')) {
    return { valid: false, error: 'Invalid URL protocol' };
  }
  
  // Basic URL format validation
  try {
    new URL(url);
    return { valid: true };
  } catch {
    return { valid: false, error: 'Invalid URL format' };
  }
};

/**
 * Generate secure random string
 * @param length - Length of the string to generate
 * @returns Secure random string
 */
export const generateSecureToken = (length: number = 32): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    for (let i = 0; i < length; i++) {
      result += chars[array[i] % chars.length];
    }
  } else {
    // Fallback for environments without crypto.getRandomValues
    for (let i = 0; i < length; i++) {
      result += chars[Math.floor(Math.random() * chars.length)];
    }
  }
  
  return result;
};

/**
 * Rate limiting implementation
 */
export class RateLimiter {
  private requests: Map<string, number[]> = new Map();
  
  /**
   * Check if request is within rate limit
   * @param identifier - Unique identifier for the request (IP, user ID, etc.)
   * @param maxRequests - Maximum requests allowed
   * @param windowMs - Time window in milliseconds
   * @returns Whether request is allowed
   */
  isAllowed(
    identifier: string, 
    maxRequests: number = SECURITY_CONFIG.MAX_REQUESTS_PER_WINDOW,
    windowMs: number = SECURITY_CONFIG.RATE_LIMIT_WINDOW
  ): boolean {
    const now = Date.now();
    const requests = this.requests.get(identifier) || [];
    
    // Remove old requests outside the window
    const validRequests = requests.filter(time => now - time < windowMs);
    
    // Check if under limit
    if (validRequests.length >= maxRequests) {
      return false;
    }
    
    // Add current request
    validRequests.push(now);
    this.requests.set(identifier, validRequests);
    
    return true;
  }
  
  /**
   * Clear old entries to prevent memory leaks
   */
  cleanup(): void {
    const now = Date.now();
    const maxAge = SECURITY_CONFIG.RATE_LIMIT_WINDOW * 2;
    
    for (const [key, requests] of this.requests.entries()) {
      const validRequests = requests.filter(time => now - time < maxAge);
      if (validRequests.length === 0) {
        this.requests.delete(key);
      } else {
        this.requests.set(key, validRequests);
      }
    }
  }
}

// Global rate limiter instance
export const rateLimiter = new RateLimiter();

/**
 * Content Security Policy (CSP) configuration
 */
export const CSP_CONFIG = {
  'default-src': ["'self'"],
  'script-src': ["'self'", "'unsafe-inline'", "https://cdn.jsdelivr.net"],
  'style-src': ["'self'", "'unsafe-inline'", "https://fonts.googleapis.com"],
  'font-src': ["'self'", "https://fonts.gstatic.com"],
  'img-src': ["'self'", "data:", "https:", "blob:"],
  'connect-src': ["'self'", "https://*.supabase.co"],
  'frame-src': ["'none'"],
  'object-src': ["'none'"],
  'base-uri': ["'self'"],
  'form-action': ["'self'"],
  'frame-ancestors': ["'none'"],
  'upgrade-insecure-requests': true,
} as const;

/**
 * Security headers configuration
 */
export const SECURITY_HEADERS = {
  'X-Content-Type-Options': 'nosniff',
  'X-Frame-Options': 'DENY',
  'X-XSS-Protection': '1; mode=block',
  'Referrer-Policy': 'strict-origin-when-cross-origin',
  'Permissions-Policy': 'camera=(), microphone=(), geolocation=()',
  'Strict-Transport-Security': 'max-age=31536000; includeSubDomains',
} as const;

/**
 * Zod schemas for input validation
 */
export const securitySchemas = {
  email: z.string()
    .min(1, 'Email is required')
    .max(254, 'Email too long')
    .email('Invalid email format')
    .transform(sanitizeInput),
    
  password: z.string()
    .min(SECURITY_CONFIG.PASSWORD_MIN_LENGTH, `Password must be at least ${SECURITY_CONFIG.PASSWORD_MIN_LENGTH} characters`)
    .refine((pwd) => validatePassword(pwd).valid, {
      message: 'Password does not meet security requirements'
    }),
    
  url: z.string()
    .min(1, 'URL is required')
    .max(SECURITY_CONFIG.MAX_URL_LENGTH, 'URL too long')
    .refine((url) => validateUrl(url).valid, {
      message: 'Invalid URL format'
    })
    .transform(sanitizeInput),
    
  text: z.string()
    .max(SECURITY_CONFIG.MAX_TEXT_LENGTH, 'Text too long')
    .transform(sanitizeInput),
    
  string: z.string()
    .max(SECURITY_CONFIG.MAX_STRING_LENGTH, 'String too long')
    .transform(sanitizeInput),
} as const;




